import { axiosInstance } from "@/lib/axios";
import type { AuthConfig, LoginInput, LoginResponse, SessionResponse, User } from "./types";
import { refreshCsrfToken } from "@/lib/csrf";

export const login = async (input: LoginInput): Promise<LoginResponse> => {
  const { data } = await axiosInstance.post<LoginResponse>(
    "/api/auth/login",
    input,
  );
  // Refresh CSRF token after login to get a fresh token for the new session
  await refreshCsrfToken();
  return data;
};

export const logout = async (): Promise<void> => {
  await axiosInstance.post("/api/auth/logout");
  // Refresh CSRF token after logout
  await refreshCsrfToken();
};

/**
 * Only the API knows whether the session is alive: its cookies are httpOnly on
 * its own host. (The JS-readable `logged_in` marker is NOT consulted — it sits
 * on the shared parent domain, and any sibling app's logout clears it, which
 * used to log people out of here with a "session expired" they never had.) A
 * dead or missing session answers 401, which the axios layer turns into the
 * redirect — and into the toast, but only if this browser actually had one.
 */
export const fetchSession = async (): Promise<User | null> => {
  const { data } =
    await axiosInstance.get<SessionResponse>("/api/auth/session");
  return data.user;
};

export interface Session {
  tokenId: string;
  createdAt: string;
  expiresAt: string;
  ipAddress?: string;
  userAgent?: string;
  isCurrent?: boolean;
}

export type SessionsResponse = {
  success: boolean;
  sessions: Session[];
};

export const getSessions = async (): Promise<Session[]> => {
  const { data } =
    await axiosInstance.get<SessionsResponse>("/api/auth/sessions");
  return data.sessions;
};

export const revokeSession = async (tokenId: string): Promise<void> => {
  await axiosInstance.delete(`/api/auth/sessions/${tokenId}`);
};

export const getAuthConfig = async (): Promise<AuthConfig> => {
  const { data } = await axiosInstance.get<AuthConfig>("/api/auth/config");
  return data;
};
